// Modules
import { useApolloClient } from '@apollo/client'
import { useLogInfoStore } from 'redesign/store'
import {
    CreateLogin,
    ResendToken,
    ResendTokenV2,
    ConfirmToken,
    AssociateCip,
    CreatePatientAccount,
} from 'services/gql/account/mutation'
import {
    CheckAccount,
    ContactsToReset,
    SearchLoginByCPF,
    ValidateLoginContact,
    VerifyPreUserRegistration,
} from 'services/gql/account/query'
import {
    ConfirmTokenType,
    ConfirmTokenData,
    PatientInfoInput,
    PatientInfoSearch,
    ContactType,
    ContactLoginStatus,
    ValidateLoginContactResponse,
    PatientLogin,
    CheckAccountType,
    CheckAccountResponse,
    ResendTokenType,
    CreatePatientInput,
    CreatePatientType,
    CreateLoginResponse,
    CreateLoginType,
    AssociateCipType,
    TokenCIPInput,
    ContactsToResetType,
    ContactsToResetResponse,
} from 'typings/account'

export const useAccountService = () => {
    const client = useApolloClient()
    const { logInfo } = useLogInfoStore()

    const verifyPreUserRegistration = async (patient: PatientInfoInput): Promise<PatientInfoSearch> => {
        const { data } = await client.query<PatientInfoSearch>({
            query: VerifyPreUserRegistration,
            fetchPolicy: 'no-cache',
            variables: {
                patient: {
                    name: patient.name,
                    cpf: patient.cpf,
                    birthDate: patient.birthDate || '',
                },
            },
        })

        return data as PatientInfoSearch
    }

    const confirmToken = async (confirmTokenData: ConfirmTokenType): Promise<ConfirmTokenData> => {
        const { data } = await client.mutate<ConfirmTokenData>({
            mutation: ConfirmToken,
            variables: {
                dataConfirmation: confirmTokenData,
                logInfo,
            },
        })

        return data as ConfirmTokenData
    }

    const viewerSearchLoginByCPF = async (username: string): Promise<void> => {
        await client.query({
            query: SearchLoginByCPF,
            fetchPolicy: 'no-cache',
            variables: {
                username,
            },
        })
    }

    const viewerSearchPatient = async (patient: PatientInfoInput) => {
        const { viewerSearch } = await verifyPreUserRegistration(patient)

        return viewerSearch
    }

    const validateLoginContact = async (contactType: ContactType, contactValue: string): Promise<ContactLoginStatus> => {
        const { data } = await client.query<ValidateLoginContactResponse>({
            query: ValidateLoginContact,
            fetchPolicy: 'no-cache',
            variables: {
                contactType,
                contactValue,
            },
        })

        const {
            validateLoginContact: { status },
        } = data as ValidateLoginContactResponse

        return status
    }

    const createLogin = async (patient: PatientLogin): Promise<CreateLoginResponse> => {
        const { data } = await client.mutate<CreateLoginType>({
            mutation: CreateLogin,
            variables: {
                patient,
                logInfo,
            },
        })

        const { createLogin: createLoginResponse } = data as CreateLoginType

        return { createLoginResponse }
    }

    const checkAccountService = async (userName: string, birthDate?: string): Promise<CheckAccountResponse> => {
        const {
            data: { checkAccount },
        } = await client.query<CheckAccountType>({
            query: CheckAccount,
            fetchPolicy: 'no-cache',
            variables: {
                userName: userName,
                birthDate: birthDate?.includes('*') ? '' : birthDate,
            },
        })

        return checkAccount
    }

    const resendToken = async (patient: ResendTokenType): Promise<void> => {
        await client.mutate<ResendTokenType>({
            mutation: ResendToken,
            variables: {
                patient,
                logInfo,
            },
        })
    }

    const resendTokenV2 = async (patient: ResendTokenType): Promise<void> => {
        await client.mutate<ResendTokenType>({
            mutation: ResendTokenV2,
            variables: {
                patient,
                logInfo,
            },
        })
    }

    const createPatientAccount = async (patientProps: CreatePatientInput): Promise<CreatePatientType> => {
        const { data } = await client.mutate({
            mutation: CreatePatientAccount,
            variables: {
                patient: patientProps,
                logInfo,
            },
        })
        return data
    }

    const associateCip = async ({ tokenCIP }: TokenCIPInput): Promise<AssociateCipType> => {
        const { data } = await client.mutate({
            mutation: AssociateCip,
            variables: {
                tokenCIP,
            },
        })

        return data
    }

    const getContactsToReset = async (userName: string): Promise<ContactsToResetResponse> => {
        const {
            data: { contactsToReset },
        } = await client.query<ContactsToResetType>({
            query: ContactsToReset,
            fetchPolicy: 'no-cache',
            variables: {
                userName: userName,
            },
        })

        return contactsToReset
    }

    return {
        verifyPreUserRegistration,
        viewerSearchPatient,
        getContactsToReset,
        confirmToken,
        viewerSearchLoginByCPF,
        validateLoginContact,
        createLogin,
        checkAccountService,
        resendToken,
        resendTokenV2,
        createPatientAccount,
        associateCip,
    }
}
